import * as React from 'react';
import Dump from "../components/dump";
import useSummaryCalculator from "../helpers/useSummaryCalculator";
import { formatDisplayDate, formatPriceWithCurrency, getOfferIntervalName, getOfferPrice, isSubscription } from "../index/utils";
import { resolvePlanName } from "../index/planNameOverrides";
import { applyDiscount, discountLines, reconciledSaving, type Discount } from "../components/discount";

type Props = Readonly<{
	offer: {}
	startDate: Date
	discount?: Discount | null
}>

function Summary({offer, startDate, discount, ...otherProps}: Props) {
	const summary = useSummaryCalculator(startDate, offer);
	const currency = offer?.priceCurrency;

	// A one-off product has no billing interval and no direct debit, so the recurring
	// half of the summary does not apply — but it can still carry a joining fee.
	const subscription = isSubscription(offer);

	// The discounted price computed from the discount's percentages, so the saving shows
	// from this step (there is no sale to price yet). The exact amount is confirmed at payment.
	const { discountedTotal, hasDiscount } = applyDiscount(summary, discount);
	// Reconciled to the shown totals so struck − discount lines == discounted total exactly.
	const saving = reconciledSaving(summary.amountTotal, discountedTotal);
	const lines = discountLines(summary, discount, startDate, saving);

	return (<>
		<div className="p-6 sm:p-8">
			{/*<Dump value={summary}/>*/}

			<h3>ORDER SUMMARY</h3>
			<h5 className="mb-8 uppercase">{resolvePlanName(offer?.showcaseIri, offer?.name)}</h5>
			<ul>
				<li>Starts on {formatDisplayDate(startDate)}</li>
				{subscription && <li>1st Direct Debit: {formatDisplayDate(summary.directDebitFirstDay)}</li>}
			</ul>

			<div className="text-right">
				{formatPriceWithCurrency(getOfferPrice(offer), currency)}{subscription && <> / {getOfferIntervalName(offer)}</>}
			</div>

			{(subscription || summary.adminFee > 0) && <>
				<hr className="my-8"/>

				{subscription &&
					<div className="flex flex-wrap gap-x-8 justify-between my-4">
						<div>{summary.firstMonthPrepaid ? 'First payment:' : 'First month:'}</div>
						<div>{formatPriceWithCurrency(summary.membershipDueToday, currency)}</div>
					</div>
				}

				{subscription && summary.firstMonthPrepaid &&
					<p className="text-sm -mt-2">Covers the rest of this month plus next month, paid in advance.</p>
				}

				{summary.adminFee > 0 &&
					<div className="flex flex-wrap gap-x-8 justify-between mt-4">
						<div>Joining fee:</div>
						<div>{formatPriceWithCurrency(summary.adminFee, currency)}</div>
					</div>
				}

				{hasDiscount && lines.length > 0 &&
					<div className="mt-4 text-primary-700 font-bold">
						<div>Discount</div>
						{lines.map((line, i) =>
							<div key={i} className="flex flex-wrap gap-x-8 justify-between font-normal text-sm mt-1">
								<div>{line.label}</div>
								<div>&minus;{formatPriceWithCurrency(line.amount, currency)}</div>
							</div>
						)}
					</div>
				}
			</>}
		</div>

		<div className="p-8 bg-primary-200 font-bold text-xl">
			<div className="flex flex-wrap gap-x-8 justify-between">
				{/* An estimate: the exact, API-calculated total is shown at the payment step. */}
				<div>Estimated total:</div>
				<div>
					{hasDiscount &&
						<span className="line-through font-normal text-base opacity-60 mr-2">{formatPriceWithCurrency(summary.amountTotal, currency)}</span>
					}
					{formatPriceWithCurrency(hasDiscount ? discountedTotal : summary.amountTotal, currency)}
				</div>
			</div>
			{hasDiscount &&
				<div className="text-sm font-normal mt-1">You save {formatPriceWithCurrency(saving, currency)}</div>
			}
		</div>
	</>);
}

export default Summary;
